// 🔧 volunteers_next.js
// ✅ Features:
// - Displays all volunteer slots sorted chronologically
// - Shows assigned volunteers for each slot
// - Loads all activities and timeSlots from /events/{eventId}/volunteers
// - Loads user data from global /users
// - Groups volunteers by upcoming start time
// - Displays activity name above grouped slots when multiple activities share the same start time
// - Sorts by ascending start time
// - Displays full name and email for each assigned user
// - Supports real-time updates
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { formatDateTime } from "../../utils/date_format";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';

const VolunteersNext = () => {

  const [activities, setActivities] = useState([]);
  const {
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  // 🔄 Load activities and timeSlots
  useEffect(() => {
    if (!eventId) return;

    const fetchActivities = async () => {
      try {
        const snap = await getDocs(collection(db, "events", eventId, "volunteers"));
        const acts = await Promise.all(
          snap.docs.map(async (docSnap) => {
            const activity = { id: docSnap.id, ...docSnap.data() };
            const slotSnap = await getDocs(
              collection(db, "events", eventId, "volunteers", docSnap.id, "timeSlots")
            );
            activity.timeSlots = slotSnap.docs.map((s) => ({
              id: s.id,
              ...s.data(),
            }));
            return activity;
          })
        );
        setActivities(acts);
      } catch (err) {
        logger.error("❌ Failed to load activities: Cannot load volunteer data (check permissions)", {
          value: err
        });
      }
    };

    fetchActivities();

  }, [eventId]);

  if (!ready || eventLoading || !eventId) {
    return <FullPageSpinner message="Loading Volunteers..." />;
  }

  // 📦 Flatten timeSlots across all activities and sort them by startTime
  const flattenedSlots = activities
    .flatMap((activity) =>
      activity.timeSlots.map((slot) => ({
        ...slot,
        activityTitle: activity.title,
        activityId: activity.id,
      }))
    )
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime)); // ⏱️ Chronological order

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">👀 Upcoming Volunteer Slots</h1>
          <p className="lead text-muted">
            {eventDoc?.name}
          </p>
          <p className="lead text-muted">Time-ordered view of volunteers by slot & activity</p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {flattenedSlots.length === 0 ? (
          <p className="text-muted">No volunteer time slots found for this event.</p>
        ) : (
          flattenedSlots.map((slot, index) => {
          const volunteerDetails = (slot.assignedUsers || [])
            .filter(Boolean)
            .sort((a, b) =>
              `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(
                `${b.firstName || ""} ${b.lastName || ""}`
              )
            );

            return (
              <div key={slot.id || index} className="mb-4 p-3 border-start border-3 bg-light">
                <h5 className="mb-1">
                  ⏱️ {formatDateTime(slot.startTime)} – {formatDateTime(slot.endTime)}
                </h5>
                <h6 className="text-primary mb-2">📌 {slot.activityTitle}</h6>

                {volunteerDetails.length > 0 ? (
                  <ul className="mb-0">
                    {volunteerDetails.map((user) => (
                      <li key={user.uid}>
                        {user.firstName} {user.lastName} ({user.email})
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted">— No volunteers assigned</p>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
};

export default VolunteersNext;
